import { Injectable, computed, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { PlanService } from './plan.service';

/**
 * Permission Service
 * Provides helper methods for checking permissions throughout the application
 * 
 * TWO-TIER PERMISSION SYSTEM:
 * 1. Plan/Subscription Level (FIRST): What features are available to the tenant
 * 2. RBAC Level (SECOND): What the user can do within those features
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly auth = inject(AuthService);
  private readonly plan = inject(PlanService);

  /**
   * Check if user has a specific permission
   * TWO-TIER CHECK:
   * 1. First check if resource is available in plan
   * 2. Then check if user has RBAC permission
   * @param permission Permission string in format "resource:action"
   */
  hasPermission(permission: string): boolean {
    const [resource] = permission.split(':');
    
    // PLATFORM OWNER / SUPER ADMIN: always allow in UI (backend still enforces RBAC).
    const roles = this.auth.roles();
    const isPlatformOrSuperAdmin = roles.some(r => {
      const name = r.toLowerCase();
      return (
        name === 'platform_owner' ||
        name === 'platform owner' ||
        name === 'super_admin' ||
        name === 'super admin'
      );
    });
    if (isPlatformOrSuperAdmin) {
      return true;
    }

    // If we don't yet have any permission info (e.g. /me not loaded),
    // don't aggressively hide UI. Let the backend enforce RBAC.
    const userPermissions = this.auth.permissions();
    if (!userPermissions || userPermissions.length === 0) {
      return true;
    }

    // FIRST TIER: Check if resource is available in plan (only if plan is known)
    const planFeatures = this.plan.planFeatures();
    if (planFeatures && !this.plan.canAccessResource(resource)) {
      return false;
    }
    
    // SECOND TIER: Check RBAC permission
    return this.auth.hasPermission()(permission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param permissions Array of permission strings
   */
  hasAnyPermission(permissions: string[]): boolean {
    return this.auth.hasAnyPermission()(permissions);
  }

  /**
   * Check if user has all of the specified permissions
   * @param permissions Array of permission strings
   */
  hasAllPermissions(permissions: string[]): boolean {
    const userPermissions = this.auth.permissions();
    return permissions.every(perm => userPermissions.includes(perm));
  }

  /**
   * Check if user can perform an action on a resource
   * TWO-TIER CHECK:
   * 1. First check if resource is available in plan (subscription level)
   * 2. Then check if user has RBAC permission
   * @param resource Resource name (e.g., 'users', 'tasks')
   * @param action Action name (e.g., 'create', 'read', 'update', 'delete')
   */
  can(resource: string, action: string): boolean {
    // FIRST TIER: Check if resource is available in plan
    if (!this.plan.canAccessResource(resource)) {
      return false;
    }
    
    // SECOND TIER: Check RBAC permission
    return this.hasPermission(`${resource}:${action}`);
  }

  /**
   * Check if user can create a resource
   */
  canCreate(resource: string): boolean {
    return this.can(resource, 'create');
  }

  /**
   * Check if user can read/view a resource
   */
  canRead(resource: string): boolean {
    return this.can(resource, 'read');
  }

  /**
   * Check if user can update a resource
   */
  canUpdate(resource: string): boolean {
    return this.can(resource, 'update');
  }

  /**
   * Check if user can delete a resource
   */
  canDelete(resource: string): boolean {
    return this.can(resource, 'delete');
  }

  /**
   * Check if user can list a resource
   */
  canList(resource: string): boolean {
    return this.can(resource, 'list');
  }

  /**
   * Get all permissions for a specific resource
   */
  getResourcePermissions(resource: string): string[] {
    const userPermissions = this.auth.permissions();
    return userPermissions
      .filter(perm => perm.startsWith(`${resource}:`))
      .map(perm => perm.split(':')[1]);
  }

  /**
   * Check if user has any permission for a resource
   */
  hasAnyResourcePermission(resource: string): boolean {
    return this.getResourcePermissions(resource).length > 0;
  }
}

